import React from 'react';
import { Card, Popover } from 'antd';

import { SegmentationPredictionToCompare } from 'models/predictions';
import ImageComparator from 'components/Shared/components/imageComparator';
import ReviewControls, { ReviewChoice } from './ReviewControls';
import SegmentationDetails from './SegmentationDetails';
import { transformSegmentationData, getExistingReview } from '../utils/predictionTransforms';

type ReviewableObject = {
  uuid: string;
  icid: string;
};

interface PredictionCardProps {
  segmentationPrediction: SegmentationPredictionToCompare;
  isBlinded: boolean;
  onReview: (
    left: ReviewableObject,
    right: ReviewableObject,
    choice: ReviewChoice,
    comment?: string
  ) => Promise<void>;
  onDicomClick: (dicomIcid: string) => void;
}

const PredictionCard: React.FC<PredictionCardProps> = ({
  segmentationPrediction,
  isBlinded,
  onReview,
  onDicomClick,
}) => {
  const { leftSegmentation, rightSegmentation, frame } = transformSegmentationData(
    segmentationPrediction,
    isBlinded
  );

  const existingReview = getExistingReview(segmentationPrediction, isBlinded);

  // pick the first segmentation from each side as the “review target”
  const left = leftSegmentation && leftSegmentation.length > 0 ? leftSegmentation[0] : null;

  const right = rightSegmentation && rightSegmentation.length > 0 ? rightSegmentation[0] : null;

  const leftObj: ReviewableObject = {
    uuid: left?.uuid ?? '',
    icid: left?.icid ?? '',
  };

  const rightObj: ReviewableObject = {
    uuid: right?.uuid ?? '',
    icid: right?.icid ?? '',
  };

  return (
    <Card
      title={
        <div>
          <strong>{segmentationPrediction.segmentationClass}</strong>
          <Popover
            content={
              <SegmentationDetails
                segmentationPrediction={segmentationPrediction}
                isBlinded={isBlinded}
                onDicomClick={onDicomClick}
              />
            }
            title="Details"
            placement="bottomRight"
          >
            <p
              style={{
                fontSize: '12px',
                color: '#666',
                width: '130px',
                marginTop: '8px',
              }}
            >
              {`${(segmentationPrediction as any).dicom?.icid || ''}${
                frame?.index !== undefined ? `_${frame.index}` : ''
              }`}
            </p>
          </Popover>
        </div>
      }
      style={{ minHeight: 400, padding: '8px' }}
    >
      <ImageComparator
        leftFrame={frame}
        rightFrame={frame}
        leftSegmentations={leftSegmentation}
        rightSegmentations={rightSegmentation}
        groundTruthSide={isBlinded ? null : 'left'}
        shuffledSegmentations={false}
        onShuffleToggle={() => {
          // Shuffle toggle disabled
        }}
        onShuffle={() => {
          // Shuffle disabled
        }}
        isBlinded={isBlinded}
        // left should highlight the returned label icid if present
        highlightedSegmentationIdLeft={
          (segmentationPrediction as any).label?.icid ||
          (segmentationPrediction as any).activeRegion?.icid ||
          (leftSegmentation && leftSegmentation.length > 0
            ? (leftSegmentation[0] as any).icid
            : undefined)
        }
        // right should highlight the returned prediction icid if present, otherwise fallback to activeRegion or first
        highlightedSegmentationIdRight={
          (segmentationPrediction as any).prediction?.icid ||
          (segmentationPrediction as any).activeRegion?.icid ||
          (rightSegmentation && rightSegmentation.length > 0
            ? (rightSegmentation[0] as any).icid
            : undefined)
        }
        // highlighted paths: prefer explicit top-level paths, otherwise find matching region paths
        highlightedSegmentationPathsLeft={
          (segmentationPrediction as any).label?.paths ||
          (leftSegmentation && leftSegmentation.length > 0
            ? (
                leftSegmentation.find(
                  s => s.icid === (segmentationPrediction as any).label?.icid
                ) as any
              )?.paths || (leftSegmentation[0] as any).paths
            : undefined)
        }
        highlightedSegmentationPathsRight={
          (segmentationPrediction as any).prediction?.paths ||
          (rightSegmentation && rightSegmentation.length > 0
            ? (
                rightSegmentation.find(
                  s => s.icid === (segmentationPrediction as any).prediction?.icid
                ) as any
              )?.paths ||
              (segmentationPrediction as any).activeRegion?.paths ||
              (rightSegmentation[0] as any).paths
            : undefined)
        }
        // also provide class value so comparator can auto-highlight matching class on render
        highlightedSegmentationClass={segmentationPrediction.segmentationClass}
        highlightedSegmentationFillColorLeft={
          (segmentationPrediction as any).segmentationColor ||
          (segmentationPrediction as any).label?.segmentationColor ||
          undefined
        }
        highlightedSegmentationFillColorRight={
          (segmentationPrediction as any).segmentationColor ||
          (segmentationPrediction as any).prediction?.segmentationColor ||
          undefined
        }
      />
      <ReviewControls
        onReview={(choice, comment) => onReview(leftObj, rightObj, choice, comment)}
        existingReview={existingReview}
        groundTruthSide={isBlinded ? null : 'left'}
        isBlinded={isBlinded}
      />
    </Card>
  );
};

export default PredictionCard;
